import compact from 'lodash/compact';
import {createSelector} from 'reselect';
import {
  BILLING,
  CONTACT_CDV,
  CONTACT_HSD,
  CONTACT_VID,
  CONTACT_XH,
} from 'src/shared/constants/scheduleCallbackIntents';
import {formatDateForDisplaywithRange} from 'src/shared/utils/date';
import {account} from 'src/shared/account/selectors';
import {outages} from 'src/shared/outages/selectors';
import {params} from 'src/shared/params/selectors';
import {services} from 'src/shared/user/selectors';
import {createResourceSelector} from 'src/shared/utils/resource';

const app = createResourceSelector('app');

const interactions = createSelector(
  app,
  app => app.interactions
);

const ONE_DAY = 24 * 60 * 60 * 1000;

const timeWithValidation = (start, end) => {
  const startTime = new Date(start);
  const endTime = new Date(end);
  return {
    time: formatDateForDisplaywithRange(startTime, endTime),
    timeValid: !!start && startTime.getTime() > Date.now() - ONE_DAY,
  };
};

const interactionAppointment = createSelector(
  app,
  app => {
    if (app.interaction) {
      return {
        email: app.interaction.email,
        emailSent: app.interaction.emailSent,
        ...app.interaction,
        ...timeWithValidation(app.interaction.interaction?.time || app.interaction.time,
          app.interaction.interaction?.endTime || app.interaction.endTime),
      };
    }
  }
);

export const interactionsAppointment = createSelector(
  interactions,
  interactions => {
    if (interactions && interactions.length > 0) {
      const interaction = interactions[0];
      return {
        ...interaction,
        ...timeWithValidation(interaction.time, interaction.endTime),
      };
    }
  }
);

export const appointment = createSelector(
  [interactionAppointment, interactionsAppointment],
  (interactionAppointment, interactionsAppointment) => interactionAppointment || interactionsAppointment
);

export const hasAppointment = createSelector(
  appointment,
  appointment => {
    // when the appointment is first created, it is stored inside of the `interation` obj
    // when we are coming back and they have one pending, the interaction becomes the entire appointment obj
    return !!appointment && appointment.interaction
      ? (appointment.interaction?.type === 'SCHEDULED_CALLBACK' || appointment.interaction?.type === 'IMMEDIATE_CALLBACK')
      : (appointment?.type === 'SCHEDULED_CALLBACK' || appointment?.type === 'IMMEDIATE_CALLBACK');
  }
);

export const phone = createSelector(
  [account, app],
  (account, app) => app.phone || ''
);

export const email = createSelector(
  [account, app],
  (account, app) => app.email || ''
);

export const flow = createSelector(
  app,
  app => app.flow
);

export const intent = createSelector(
  [app, params],
  (app, params) => app.intent || params?.combined?.selectedIntent || ''
);

export const shouldShowIntents = createSelector(
  [intent, params],
  (intent, params) => !intent || !params.combined.selectedIntent
);

export const day = createSelector(
  app,
  app => app.day
);

export const timeslots = createSelector(
  app,
  app => app.timeslots
);

export const availableTimes = createSelector(
  [day, timeslots],
  (day, timeslots) => timeslots?.[day]?.times || []
);

export const timeslot = createSelector(
  [app, availableTimes],
  (app, times) => app.timeslot || times?.[0]?.isoFormat || '');

export const availableIntents = createSelector(
  services,
  services => compact([
    services.hsdStatus && {
      title: 'Internet',
      description: 'For help with slow speeds, WiFi connectivity issues, and device compatibility questions.',
      value: CONTACT_HSD,
      subtitle: 'An Internet',
      intent: 'skill.xfi.internet.learn.entry',
    },
    services.videoStatus && {
      title: 'TV',
      description: 'For help with service issues like on-screen errors, playback issues, and more.',
      value: CONTACT_VID,
      subtitle: 'A TV',
      intent: 'skill.cxao.tv.learn.entry',
    },
    services.cdvStatus && {
      title: 'Voice',
      description: 'For help with setup, dial tone errors, and more.',
      value: CONTACT_CDV,
      subtitle: 'An Xfinity Voice',
      intent: 'skill.repair.voice',
    },
    services.homeSecurityStatus && {
      title: 'Home Security and Automation',
      description: 'For help arming and disarming your system, troubleshooting, and more.',
      value: CONTACT_XH,
      subtitle: 'A Home Security and Automation',
      intent: 'skill.xh.landing',
    },
    {
      title: 'Account and Billing',
      description: 'For help with billing issues, managing your plan, and more.',
      value: BILLING,
      subtitle: 'An Account and Billing',
      intent: 'skill.billing.account',
    },
  ])
);

const serviceNames = {
  channelOutage: 'channel',
  emailOutage: 'email service',
  homeOutage: 'home security service',
  internetOutage: 'internet service',
  onDemandOutage: 'on demand service',
  tvOutage: 'tv service',
  voiceOutage: 'voice service',
};

export const outageMessage = createSelector(
  [outages],
  ({summary = {}}) => {
    const servicesAffected = Object.keys(serviceNames).reduce((acc, key) => {
      if (summary[key]) {
        acc.push(serviceNames[key]);
      }
      return acc;
    }, []);
    if (servicesAffected.length > 1) {
      return `We apologize for any inconvenience, and we're working to get your services up and running soon. Questions or concerns? Our agents are here to help.`;
    }
    if (servicesAffected.length === 1) {
      return `We apologize for any inconvenience, and we're working to get your ${servicesAffected[0]} up and running soon. Questions or concerns? Our agents are here to help.`;
    }
    return '';
  }
);
